import React, { useState, useMemo, useRef } from 'react'
import { Box, Button, CircularProgress, FormControl, FormControlLabel, Grid, Paper, Radio, RadioGroup, Typography } from '@mui/material'
import { useStyles } from './style'
import PromotedItemsModal from './promoted-items-modal'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { ArrowUpward, Star, Settings, OpenInNew } from '@mui/icons-material'
import CatalogItem from '../../pages/content/catalog-item'
import { BannerStatic } from '..'
import { Link } from 'react-router-dom'

const noop = (mode) => console.log(mode)

const TemplatePromotedContent = ({ template, onPromotedContentModeChanged = noop }) => {
  const classes = useStyles()
  const [isPromotedModalOpen, setPromotedModalOpen] = useState(false)
  const [promotedContent, setPromotedContent] = useState([])
  const selectedPromotedContent = useMemo(() => promotedContent.map(c => c.content), [promotedContent])
  const [mode, setMode] = useState(template.promotedContentMode || 'disabled')
  const oldMode = useRef(mode)
  const isChildTemplate = !!template?.parent?.sk

  const [getPromotedContent] = useEvent([{
    eventName: 'promotedContentGetPromotedContent',
    onSuccess: (payload) => {
      setPromotedContent(payload.promotedContent)
    },
    onError: (err) => {
      console.log('Error getting promoted content ', err)
      BannerStatic.show({ label: 'Error fetching promoted content', isError: true })
    }
  }])

  const [templateUpdate] = useEvent([{
    eventName: 'templateUpdate',
    onSuccess: ({ template }) => {
      setMode(template.promotedContentMode)
      onPromotedContentModeChanged && onPromotedContentModeChanged(template.promotedContentMode)
      BannerStatic.show({ label: 'Template promoted content updated' })
    },
    onError: (err) => {
      console.log('Error updating template', err)
      setMode(oldMode.current)
      BannerStatic.show({ label: 'Error updating template promoted content', isError: true })
    }
  }])

  useEventsOnViewLoad(() => {
    getPromotedContent.trigger({
      template,
      verifyFeatureToggle: false
    })
  }, [getPromotedContent])

  const handlePromotedContentModeChange = (evt) => {
    oldMode.current = mode
    const newMode = evt.target.value
    setMode(newMode)
    templateUpdate.trigger({
      sk: template.sk,
      promotedContentMode: newMode
    })
  }

  const handleClosePromotedModal = () => setPromotedModalOpen(false)

  const handleCloseOkPromotedModal = () => {
    setPromotedModalOpen(false)
    getPromotedContent.trigger({
      template,
      verifyFeatureToggle: false
    })
  }
  const handleOpenPromotedModal = () => setPromotedModalOpen(true)

  if (!template || getPromotedContent.isWorking) {
    return (
      <Grid container justifyContent='center' alignItems='center' gap={1}>
        <CircularProgress size={35} />
        <Typography variant='body2' color='textSecondary'>
          Loading Featured Content...
        </Typography>
        <Box sx={{ marginY: 4 }} />
      </Grid>
    )
  }

  return (
    <>
      <PromotedItemsModal
        open={isPromotedModalOpen}
        onClose={handleClosePromotedModal}
        onCloseOk={handleCloseOkPromotedModal}
        selected={selectedPromotedContent}
        template={template}
      />
      <Box data-test='promoted-content'>
        <Grid container className={classes.titleCtn}>
          <Grid item xs={10}>
            <Typography variant='subtitle1'>Featured Content</Typography>
          </Grid>
        </Grid>
        <Box display='flex' flexDirection='row' alignItems='center'>
          <Typography variant='body2' color='textSecondary' style={{ display: 'flex', flex: 1, marginRight: '12px', paddingBottom: mode === 'disabled' ? 20 : 0 }}>
            Select 5 pieces of content to be highlighted on the homepage. The first two selections will also be included in the Weekly Email sent to users.
          </Typography>
          <FormControl>
            <RadioGroup
              row
              value={mode}
              data-test='promoted-content-mode-selector'
              onChange={handlePromotedContentModeChange}
              aria-label='Promoted content mode'
              id='promoted-content-mode'
            >
              <FormControlLabel value='disabled' control={<Radio />} data-test='promoted-content-mode-disabled' disabled={templateUpdate.isWorking} label='Disabled' />
              {isChildTemplate && <FormControlLabel value='inherit' data-test='promoted-content-mode-inherit' disabled={templateUpdate.isWorking} control={<Radio />} label='Default' />}
              <FormControlLabel value='enabled' control={<Radio />} data-test='promoted-content-mode-enabled' disabled={templateUpdate.isWorking} label={isChildTemplate ? 'Custom' : 'Enabled'} />
            </RadioGroup>
          </FormControl>
        </Box>

        {mode === 'inherit' && template?.parent?.sk && (
          <Box sx={{ marginTop: '16px' }} data-test='promoted-content-inherit-message'>
            <Typography color='textSecondary' style={{ display: 'flex', flex: 1, fontSize: '14px', paddingBottom: 20 }}>
              This template is using the default featured content. To customize, select Custom.
              You can check the default featured content clicking&nbsp;
              <Link to={`${process.env.PUBLIC_URL}/templates/${template?.parent?.sk}`} style={{ display: 'flex' }} target='_blank'>here<OpenInNew /></Link>
            </Typography>
          </Box>
        )}

        {(mode === 'enabled' && !templateUpdate.isWorking) && (
          <>
            <Box display='flex' mt={2} mb={2} flex={1} justifyContent='end'>
              <Button variant='outlined' color='primary' data-test='promoted-content-enabled-manage' onClick={handleOpenPromotedModal} startIcon={<Settings />}>Manage</Button>
            </Box>

            {(promotedContent && promotedContent.length > 0) && (
              <Box>
                <Grid container className={classes.selectedContentRoot}>
                  <Paper
                    id='catalog-list'
                    classes={{ root: classes.tablePaper }}
                    tabIndex={0}
                    aria-label='Content List'
                    elevation={1}
                  >
                    <Grid
                      id='catalog-list-headers'
                      container
                      tabIndex={0}
                      direction='row'
                      alignItems='center'
                      className={classes.gridHeader}
                      classes={{ root: classes.tableHead }}
                      aria-label='List headers, Title, Type, Last update'
                    >
                      <Grid item md={8}>Title</Grid>
                      <Grid item md={2}>Type</Grid>
                      <Grid item md={2} className={classes.dateUpdatedHeader}>
                        <ArrowUpward fontSize='small' />
                        <span>Last update</span>
                      </Grid>
                    </Grid>
                    {promotedContent.map((row, key) => {
                      const nameAdornment = key === 0 && (
                        <>
                          <Star fontSize='small' className={classes.mainPromotedIcon} />
                          <span className={classes.mainPromotedText}>Main Promoted Card</span>
                        </>
                      )
                      return (
                        <CatalogItem
                          key={key}
                          index={key}
                          onClick={() => { }}
                          article={row.content}
                          classes={classes}
                          nameAdornment={nameAdornment}
                          lastOne={key === promotedContent.length - 1}
                          name={row.sk}
                        />
                      )
                    })}
                  </Paper>
                </Grid>
              </Box>
            )}
          </>
        )}
      </Box>
    </>
  )
}

export default TemplatePromotedContent
